// ── TIME AND EFFORT ──────────────────────────────────────────
// After-the-fact certification of personnel charges (2 CFR 200.430(g)(1)):
// per person per period, the percent payroll charged to each award against
// the percent of effort actually spent, signed by the employee and approved
// by the supervisor. Totals, flags, adjustments and status are worked out on
// the server (lib/effort.js).
import { useState, useEffect, useCallback } from "react";
import { TOKENS } from "../tokens";
import { FONT_H, FONT_UI, FONT_MONO, useT } from "../theme";
import { API, apiFetch } from "../utils/api";

// Same as lib/effort.js ADJUSTMENT_THRESHOLD_PCT. Used only to flag lines while editing.
const THRESHOLD = 5;
const PERIOD_TYPES = [["monthly", "Monthly"], ["semiannual", "Semiannual"], ["custom", "Custom"]];
const RED = "#dc2626", GREEN = "#059669", AMBER = "#d97706";

const newId = () => Math.random().toString(36).slice(2, 12);
const today = () => new Date().toISOString().slice(0, 10);
const blankLine = (over = {}) => ({ id: newId(), award: "", federal: true, charged: 0, actual: 0, ...over });
const blank = () => ({
  id: newId(), person: { name: "", email: "", supervisorName: "", supervisorEmail: "" },
  period: { type: "monthly", start: "", end: "" }, salary: "", lines: [blankLine()],
  employeeSignOff: null, supervisorSignOff: null,
});
const n = (v) => Number(v) || 0;
const r2 = (v) => Math.round(v * 100) / 100;
function saveFile(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function EffortPage() {
  const T = useT();
  const [certs, setCerts]     = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError]     = useState("");
  const [busy, setBusy]       = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/effort`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not load certifications.");
      setCerts(data.certifications || []);
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (extra = {}) => {
    setBusy(true); setError("");
    try {
      const { review, updatedAt, ...body } = { ...editing, ...extra };
      const res = await apiFetch(`${API}/effort/${encodeURIComponent(editing.id)}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not save.");
      setEditing(null);
      await load();
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm(`Delete the certification for ${editing.person?.name || "this person"}? This cannot be undone.`)) return;
    setBusy(true); setError("");
    try {
      const res = await apiFetch(`${API}/effort/${encodeURIComponent(editing.id)}`, { method: "DELETE" });
      if (!res.ok) throw new Error((await res.json()).error || "Could not delete.");
      setEditing(null);
      await load();
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const card  = { background: T.bgSurface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, marginBottom: 18 };
  const label = { fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.1em", textTransform: "uppercase", color: T.textMuted, marginBottom: 6, display: "block" };
  const input = { width: "100%", padding: "9px 11px", background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 8, color: T.textBody, fontSize: 13, fontFamily: FONT_UI, outline: "none", boxSizing: "border-box" };
  const btn = (primary) => ({ padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: FONT_UI, cursor: "pointer", minHeight: 38, border: `1px solid ${primary ? TOKENS.gold : T.border}`, background: primary ? TOKENS.gold : "transparent", color: primary ? "#fff" : T.textBody });
  const ui = { T, card, label, input, btn };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "22px 24px 60px", background: T.bgApp }}>
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <h1 style={{ fontFamily: FONT_H, fontSize: 24, fontWeight: 700, color: T.text, margin: "0 0 4px" }}>Time and Effort</h1>
        <p style={{ fontFamily: FONT_UI, fontSize: 13.5, color: T.textMuted, margin: "0 0 20px" }}>
          After each period, each person confirms how their time was actually spent against what payroll charged, and their supervisor approves it. Lines that differ by more than {THRESHOLD} percentage points are flagged for a payroll adjustment (2 CFR 200.430(g)(1)).
        </p>
        {error && <div role="alert" style={{ ...card, borderColor: RED, color: RED, fontFamily: FONT_UI, fontSize: 13 }}>{error}</div>}

        {editing
          ? <Editor {...ui} c={editing} setC={setEditing} onSave={save} onDelete={remove} onCancel={() => setEditing(null)} busy={busy} setError={setError} isNew={!certs.some((x) => x.id === editing.id)} />
          : <>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                <button style={btn(true)} onClick={() => setEditing(blank())}>New certification</button>
              </div>
              <CertList {...ui} certs={certs} onOpen={setEditing} setError={setError} />
            </>}
      </div>
    </div>
  );
}

function CertList({ T, card, btn, certs, onOpen, setError }) {
  if (!certs.length) return <div style={{ ...card, fontFamily: FONT_UI, fontSize: 13.5, color: T.textMuted }}>No certifications yet. Add one per person for each period that has ended.</div>;
  const th = { textAlign: "left", padding: "8px 10px", fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: T.textMuted, borderBottom: `1px solid ${T.border}` };
  const td = { padding: "10px", fontFamily: FONT_UI, fontSize: 13, color: T.textBody, borderBottom: `1px solid ${T.border}`, verticalAlign: "top" };
  const periods = {};
  for (const c of certs) {
    const k = `${c.period?.start}|${c.period?.end}`;
    periods[k] = [...(periods[k] || []), c];
  }
  const download = async (start, end) => {
    setError("");
    try {
      const res = await apiFetch(`${API}/effort/export?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`);
      if (!res.ok) throw new Error((await res.json()).error || "Could not build the forms.");
      saveFile(await res.blob(), `time-and-effort-${start}-to-${end}.xlsx`);
    } catch (e) { setError(e.message); }
  };
  return Object.entries(periods).sort(([a], [b]) => b.localeCompare(a)).map(([key, list]) => {
    const [start, end] = key.split("|");
    return (
      <div key={key} style={{ ...card, padding: 0, overflowX: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "14px 16px", flexWrap: "wrap" }}>
          <h2 style={{ fontFamily: FONT_H, fontSize: 16, color: T.text, margin: 0 }}>{start} to {end}</h2>
          <button style={btn(false)} onClick={() => download(start, end)}>Download forms and summary (.xlsx)</button>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["Employee", "Supervisor", "Status", "Lines to adjust", "Due"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {list.map((c) => {
              const r = c.review || {};
              return (
                <tr key={c.id} onClick={() => onOpen(c)} onKeyDown={(e) => e.key === "Enter" && onOpen(c)} tabIndex={0} style={{ cursor: "pointer" }}>
                  <td style={td}><strong>{c.person?.name}</strong></td>
                  <td style={td}>{c.person?.supervisorName}</td>
                  <td style={{ ...td, color: r.status === "approved" ? GREEN : T.textBody }}>{r.statusLabel}</td>
                  <td style={td}>{r.flagged ? <span style={{ color: AMBER }}>{r.flagged}</span> : "0"}</td>
                  <td style={td}>{r.due}{r.overdue && <span style={{ color: RED, marginLeft: 6 }}>overdue</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  });
}

function Editor({ T, card, label, input, btn, c, setC, onSave, onDelete, onCancel, busy, setError, isNew }) {
  const [staff, setStaff] = useState(null);
  const setIn = (k, sub, v) => setC({ ...c, [k]: { ...c[k], [sub]: v } });
  const setLine = (i, patch) => setC({ ...c, lines: c.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 14 };
  const field = (lbl, el) => <label style={{ display: "block" }}><span style={label}>{lbl}</span>{el}</label>;
  const charged = r2(c.lines.reduce((s, l) => s + n(l.charged), 0));
  const actual  = r2(c.lines.reduce((s, l) => s + n(l.actual), 0));
  const signed  = c.employeeSignOff || c.supervisorSignOff;

  const readLoe = async (file) => {
    if (!file) return;
    setError("");
    try {
      const fd = new FormData();
      fd.append("workbook", file);
      const res = await apiFetch(`${API}/effort/prefill`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not read that sheet.");
      setStaff(data.staff || []);
    } catch (e) { setError(e.message); }
  };
  const pick = (i) => {
    const s = staff[i];
    if (!s) return;
    const lines = Object.entries(s.alloc).map(([award, pct]) => blankLine({ award, charged: pct, actual: pct }));
    setC({ ...c, person: { ...c.person, name: c.person.name || s.name }, lines: lines.length ? lines : c.lines });
  };

  return (
    <div style={card}>
      <h2 style={{ fontFamily: FONT_H, fontSize: 18, color: T.text, margin: "0 0 16px" }}>{isNew ? "New certification" : `${c.person.name}: ${c.period.start} to ${c.period.end}`}</h2>
      <div style={grid}>
        {field("Employee name", <input style={input} value={c.person.name} onChange={(e) => setIn("person", "name", e.target.value)} />)}
        {field("Employee email (gets the alerts)", <input style={input} type="email" value={c.person.email || ""} onChange={(e) => setIn("person", "email", e.target.value)} />)}
        {field("Supervisor name", <input style={input} value={c.person.supervisorName || ""} onChange={(e) => setIn("person", "supervisorName", e.target.value)} />)}
        {field("Supervisor email", <input style={input} type="email" value={c.person.supervisorEmail || ""} onChange={(e) => setIn("person", "supervisorEmail", e.target.value)} />)}
      </div>
      <div style={grid}>
        {field("Period", (
          <select style={input} value={c.period.type} onChange={(e) => setIn("period", "type", e.target.value)}>
            {PERIOD_TYPES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        ))}
        {field("Start", <input style={input} type="date" value={c.period.start || ""} onChange={(e) => setIn("period", "start", e.target.value)} />)}
        {field("End", <input style={input} type="date" value={c.period.end || ""} onChange={(e) => setIn("period", "end", e.target.value)} />)}
        {field("Salary for the period ($, optional)", <input style={input} value={c.salary ?? ""} onChange={(e) => setC({ ...c, salary: e.target.value })} />)}
      </div>

      <h3 style={{ fontFamily: FONT_UI, fontSize: 14, color: T.text, margin: "20px 0 4px" }}>Awards and other activity</h3>
      <p style={{ fontFamily: FONT_UI, fontSize: 12.5, color: T.textMuted, margin: "0 0 10px" }}>
        List everything the person was paid for, including non-federal work, so both columns add up to 100.
      </p>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <label style={{ ...btn(false), display: "inline-flex", alignItems: "center" }}>
          Fill from a Level of Effort sheet
          <input type="file" accept=".xlsx,.xlsm" hidden onChange={(e) => { readLoe(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
        {staff && (
          <select style={{ ...input, width: 280 }} defaultValue="" onChange={(e) => pick(Number(e.target.value))} aria-label="Person on the LoE sheet">
            <option value="" disabled>{staff.length ? "Choose the person…" : "No staff found on that sheet"}</option>
            {staff.map((s, i) => <option key={s.staffId} value={i}>{s.name} ({s.staffId})</option>)}
          </select>
        )}
      </div>
      {c.lines.map((l, i) => {
        const diff = r2(n(l.actual) - n(l.charged));
        return (
          <div key={l.id} style={{ display: "grid", gridTemplateColumns: "minmax(180px, 2fr) repeat(3, minmax(90px, 1fr)) auto auto", gap: 10, alignItems: "end", marginBottom: 10 }}>
            {field(`Award or activity ${i + 1}`, <input style={input} value={l.award} onChange={(e) => setLine(i, { award: e.target.value })} />)}
            {field("Charged %", <input style={input} type="number" min="0" max="100" value={l.charged} onChange={(e) => setLine(i, { charged: e.target.value })} />)}
            {field("Actual %", <input style={input} type="number" min="0" max="100" value={l.actual} onChange={(e) => setLine(i, { actual: e.target.value })} />)}
            <div style={{ fontFamily: FONT_UI, fontSize: 13, minHeight: 38, display: "flex", alignItems: "center", color: Math.abs(diff) > THRESHOLD ? AMBER : T.textBody }}>
              {diff > 0 ? "+" : ""}{diff} pts{Math.abs(diff) > THRESHOLD && " adjust"}
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: FONT_UI, fontSize: 13, color: T.textBody, minHeight: 38 }}>
              <input type="checkbox" checked={l.federal !== false} onChange={(e) => setLine(i, { federal: e.target.checked })} /> Federal
            </label>
            <button style={btn(false)} onClick={() => setC({ ...c, lines: c.lines.filter((_, j) => j !== i) })}>Remove</button>
          </div>
        );
      })}
      <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
        <button style={btn(false)} onClick={() => setC({ ...c, lines: [...c.lines, blankLine({ federal: false })] })}>Add line</button>
        <span style={{ fontFamily: FONT_UI, fontSize: 13, color: charged === 100 && actual === 100 ? GREEN : RED }}>
          Totals: charged {charged}, actual {actual}
        </span>
      </div>

      <h3 style={{ fontFamily: FONT_UI, fontSize: 14, color: T.text, margin: "22px 0 10px" }}>Sign-off</h3>
      <div style={{ fontFamily: FONT_UI, fontSize: 13, color: T.textBody, lineHeight: 1.7 }}>
        <div>Employee: {c.employeeSignOff ? <span style={{ color: GREEN }}>certified by {c.employeeSignOff.name} on {c.employeeSignOff.date}</span> : "not yet certified"}</div>
        <div>Supervisor: {c.supervisorSignOff ? <span style={{ color: GREEN }}>approved by {c.supervisorSignOff.name} on {c.supervisorSignOff.date}</span> : "not yet approved"}</div>
        {signed && <div style={{ color: AMBER }}>Changing any name, date, salary or percentage clears both sign-offs.</div>}
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
        <button style={btn(true)} onClick={() => onSave()} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        {!c.employeeSignOff && <button style={btn(false)} onClick={() => onSave({ employeeSignOff: { date: today() } })} disabled={busy}>Employee certifies</button>}
        {c.employeeSignOff && !c.supervisorSignOff && <button style={btn(false)} onClick={() => onSave({ supervisorSignOff: { date: today() } })} disabled={busy}>Supervisor approves</button>}
        <button style={btn(false)} onClick={onCancel} disabled={busy}>Cancel</button>
        {!isNew && <button style={{ ...btn(false), color: RED, marginLeft: "auto" }} onClick={onDelete} disabled={busy}>Delete</button>}
      </div>
    </div>
  );
}
